import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEntity, useEntityNetwork } from '../api/entities';
import { useTimeline, useRiskScore, exportEntityPDF, useAnnotations, useCreateAnnotation, useEnrichEntity, useEnrichmentTasks } from '../api/intelligence';
import RiskGauge from '../components/visualization/RiskGauge';
import ForceGraph from '../components/graph/ForceGraph';
import type { EntityType, GraphNode, GraphEdge } from '../types';

const TYPE_BADGES: Record<string, string> = {
  person: 'bg-blue-500/20 text-blue-400',
  organization: 'bg-purple-500/20 text-purple-400',
  location: 'bg-green-500/20 text-green-400',
  vehicle: 'bg-orange-500/20 text-orange-400',
  document: 'bg-gray-500/20 text-gray-400',
  financial_account: 'bg-yellow-500/20 text-yellow-400',
  phone_number: 'bg-cyan-500/20 text-cyan-400',
  email: 'bg-teal-500/20 text-teal-400',
  ip_address: 'bg-red-500/20 text-red-400',
  domain: 'bg-orange-500/20 text-orange-400',
  cryptocurrency_wallet: 'bg-amber-500/20 text-amber-400',
  event: 'bg-violet-500/20 text-violet-400',
};

const TYPE_ICONS: Record<string, string> = {
  person: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z',
  organization: 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21',
  location: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z',
  financial_account: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z',
};

const RELATION_LABELS: Record<string, string> = {
  WORKS_FOR: 'Emploi / Fonction',
  LOCATED_AT: 'Localisation',
  PARENT_OF: 'Famille',
  ASSOCIATED_WITH: 'Associes',
  OWNS: 'Patrimoine',
  CONTROLS: 'Controle',
  AFFILIATED_WITH: 'Affiliation',
  EMPLOYS: 'Employes',
  FAMILY_OF: 'Famille',
  TRANSACTS_WITH: 'Transactions',
  COMMUNICATES_WITH: 'Communications',
  RELATED_TO: 'Lies',
  SHAREHOLDER: 'Actionnariat',
  DIRECTOR_OF: 'Direction',
  MANAGES: 'Gestion',
};

interface NetworkNode {
  id: string;
  name: string;
  entity_type: string;
  label: string;
  type: string;
  risk_score: number;
  confidence: number;
  properties: Record<string, unknown>;
}

interface NetworkEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  label: string;
  confidence: number;
  properties: Record<string, unknown>;
}

// Group relationships by type and resolve entity names
function groupRelationships(
  entityId: string,
  edges: NetworkEdge[],
  nodes: NetworkNode[],
): Record<string, Array<{ entity: NetworkNode; relation: NetworkEdge; direction: 'out' | 'in' }>> {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const groups: Record<string, Array<{ entity: NetworkNode; relation: NetworkEdge; direction: 'out' | 'in' }>> = {};

  for (const edge of edges) {
    const relType = edge.type || edge.label || 'RELATED_TO';
    if (!groups[relType]) groups[relType] = [];

    const isSource = edge.source === entityId;
    const otherId = isSource ? edge.target : edge.source;
    const otherNode = nodeMap.get(otherId);
    if (!otherNode) continue;

    groups[relType].push({
      entity: otherNode,
      relation: edge,
      direction: isSource ? 'out' : 'in',
    });
  }

  return groups;
}

// Extract specific categories for the structured view
function extractCategories(
  entityId: string,
  edges: NetworkEdge[],
  nodes: NetworkNode[],
) {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  const companies: Array<{ node: NetworkNode; role: string; relationType: string }> = [];
  const locations: Array<{ node: NetworkNode; relationType: string }> = [];
  const family: Array<{ node: NetworkNode; relation: string }> = [];
  const associates: Array<{ node: NetworkNode; relation: string }> = [];
  const assets: Array<{ node: NetworkNode; relation: string }> = [];
  const other: Array<{ node: NetworkNode; relation: string; relationType: string }> = [];

  for (const edge of edges) {
    const isSource = edge.source === entityId;
    const otherId = isSource ? edge.target : edge.source;
    const otherNode = nodeMap.get(otherId);
    if (!otherNode) continue;

    const relType = (edge.type || edge.label || '').toUpperCase();
    const entityType = (otherNode.entity_type || otherNode.type || '').toLowerCase();
    const props = edge.properties || {};

    if (entityType === 'organization') {
      const role = (props.role as string) || (props.relation as string) || relType;
      companies.push({ node: otherNode, role, relationType: relType });
    } else if (entityType === 'location') {
      locations.push({ node: otherNode, relationType: relType });
    } else if (['PARENT_OF', 'FAMILY_OF', 'CHILD_OF', 'SPOUSE_OF'].includes(relType)) {
      family.push({ node: otherNode, relation: (props.relation as string) || relType });
    } else if (['OWNS', 'CONTROLS', 'SHAREHOLDER'].includes(relType)) {
      assets.push({ node: otherNode, relation: relType });
    } else if (entityType === 'person') {
      associates.push({ node: otherNode, relation: (props.relation as string) || (props.category as string) || relType });
    } else {
      other.push({ node: otherNode, relation: relType, relationType: relType });
    }
  }

  return { companies, locations, family, associates, assets, other };
}

function InfoCard({ title, icon, children, count }: { title: string; icon: string; children: React.ReactNode; count?: number }) {
  return (
    <div className="glass-panel p-4">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-cyber-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
        </svg>
        <h3 className="text-sm font-semibold text-gray-200">{title}</h3>
        {count !== undefined && count > 0 && (
          <span className="ml-auto px-2 py-0.5 text-xs bg-cyber-cyan/10 text-cyber-cyan rounded-full">{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function EntityBadge({ node, subtitle }: { node: NetworkNode; subtitle?: string }) {
  const navigate = useNavigate();
  const type = (node.entity_type || node.type || 'document') as string;
  return (
    <button
      onClick={() => navigate(`/entities/${node.id}`)}
      className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg bg-surface-tertiary/50 hover:bg-surface-tertiary transition-colors text-left group"
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${TYPE_BADGES[type] || 'bg-gray-500/20 text-gray-400'}`}>
        {(node.name || '?').charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-200 truncate group-hover:text-cyber-cyan transition-colors">{node.name}</p>
        {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
        <p className="text-xs text-gray-600 capitalize">{type.replace(/_/g, ' ')}</p>
      </div>
      {node.risk_score > 0 && (
        <span className={`text-xs font-mono ${node.risk_score >= 0.7 ? 'text-red-400' : node.risk_score >= 0.4 ? 'text-amber-400' : 'text-green-400'}`}>
          {Math.round(node.risk_score * 100)}%
        </span>
      )}
    </button>
  );
}

export default function EntityProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [networkDepth, setNetworkDepth] = useState(3);
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'notes' | 'network' | 'relations' | 'raw'>('overview');
  const [exporting, setExporting] = useState(false);
  const [noteContent, setNoteContent] = useState('');

  const { data: entity, isLoading: entityLoading } = useEntity(id || null);
  const { data: network, isLoading: networkLoading } = useEntityNetwork(id || null, networkDepth);
  const { data: timeline, isLoading: timelineLoading } = useTimeline(id || null, 90);
  const { data: riskData } = useRiskScore(id || null);
  const { data: annotations, refetch: refetchAnnotations } = useAnnotations(id || null);
  const createAnnotation = useCreateAnnotation();
  const enrichMutation = useEnrichEntity();
  const { data: enrichTasks } = useEnrichmentTasks(id || null);

  const handleExportPDF = useCallback(async () => {
    if (!id || exporting) return;
    setExporting(true);
    try {
      await exportEntityPDF(id);
    } catch {
      // silently fail
    } finally {
      setExporting(false);
    }
  }, [id, exporting]);

  const networkNodes = useMemo(() => {
    if (!network?.nodes) return [] as NetworkNode[];
    return (network.nodes as unknown as NetworkNode[]);
  }, [network]);

  const networkEdges = useMemo(() => {
    if (!network?.edges) return [] as NetworkEdge[];
    return (network.edges as unknown as NetworkEdge[]);
  }, [network]);

  const graphNodes: GraphNode[] = useMemo(() => {
    return networkNodes.map((n) => ({
      id: String(n.id),
      label: String(n.name || n.label || ''),
      type: (n.entity_type || n.type || 'document') as EntityType,
      name: String(n.name || ''),
      risk_score: Number(n.risk_score || 0),
      confidence: Number(n.confidence || 1),
      properties: (n.properties || {}) as Record<string, unknown>,
    }));
  }, [networkNodes]);

  const graphEdges: GraphEdge[] = useMemo(() => {
    return networkEdges.map((e) => ({
      id: String(e.id),
      source: String(e.source),
      target: String(e.target),
      type: (e.type || e.label || 'related_to') as GraphEdge['type'],
      label: String(e.label || e.type || ''),
      weight: 1,
      confidence: Number(e.confidence || 1),
    }));
  }, [networkEdges]);

  const categories = useMemo(() => {
    if (!networkEdges.length || !networkNodes.length || !id) return null;
    return extractCategories(id, networkEdges, networkNodes);
  }, [networkEdges, networkNodes, id]);

  const groupedRelations = useMemo(() => {
    if (!networkEdges.length || !networkNodes.length || !id) return {};
    return groupRelationships(id, networkEdges, networkNodes);
  }, [networkEdges, networkNodes, id]);

  const props = (entity?.properties || {}) as Record<string, string | number | boolean | null | undefined | object>;
  const p = (key: string): string => {
    const v = props[key];
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  if (entityLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-gray-400">Entite introuvable</p>
        <button onClick={() => navigate('/entities')} className="px-4 py-2 text-sm bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20">
          Retour aux entites
        </button>
      </div>
    );
  }

  const tabs = [
    { key: 'overview' as const, label: 'Vue globale' },
    { key: 'timeline' as const, label: 'Chronologie' },
    { key: 'notes' as const, label: `Notes${annotations?.length ? ` (${annotations.length})` : ''}` },
    { key: 'network' as const, label: 'Reseau' },
    { key: 'relations' as const, label: 'Relations' },
    { key: 'raw' as const, label: 'Donnees brutes' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start gap-6">
        <button onClick={() => navigate('/entities')} className="mt-1 p-2 rounded-lg hover:bg-surface-tertiary text-gray-500 hover:text-gray-300 transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
        </button>

        <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold flex-shrink-0 ${TYPE_BADGES[entity.type] || 'bg-gray-500/20 text-gray-400'}`}>
          {entity.name.charAt(0).toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-100 truncate">{entity.name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${TYPE_BADGES[entity.type] || 'bg-gray-500/20 text-gray-400'}`}>
              {entity.type.replace(/_/g, ' ')}
            </span>
            {entity.source && (
              <span className="text-xs text-gray-500">Source: {entity.source}</span>
            )}
            <span className="text-xs text-gray-600">ID: {entity.id.substring(0, 8)}...</span>
          </div>
          {entity.description && (
            <p className="mt-2 text-sm text-gray-400 line-clamp-2">{entity.description}</p>
          )}
        </div>

        <div className="flex-shrink-0 flex items-center gap-3">
          <RiskGauge score={riskData?.risk_score ?? entity.risk_score} size={90} label="Risque" />
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="px-3 py-2 text-xs font-medium bg-helios-700/30 text-helios-400 rounded-lg hover:bg-helios-700/50 transition-colors disabled:opacity-50"
            title="Exporter le rapport"
          >
            {exporting ? (
              <div className="w-4 h-4 border-2 border-helios-400/30 border-t-helios-400 rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border-subtle">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 ${
              activeTab === tab.key
                ? 'text-cyber-cyan border-cyber-cyan'
                : 'text-gray-500 border-transparent hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* Identity / Profile */}
          <InfoCard
            title={entity.type === 'person' ? 'Identite' : 'Informations'}
            icon={TYPE_ICONS[entity.type] || TYPE_ICONS.person}
          >
            <div className="space-y-2">
              {p('first_name') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Prenom</span>
                  <span className="text-gray-300">{p('first_name')}</span>
                </div>
              )}
              {p('last_name') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Nom</span>
                  <span className="text-gray-300">{p('last_name')}</span>
                </div>
              )}
              {p('function') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Fonction</span>
                  <span className="text-gray-300">{p('function')}</span>
                </div>
              )}
              {p('nationality') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Nationalite</span>
                  <span className="text-gray-300">{p('nationality')}</span>
                </div>
              )}
              {p('birth_date') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Date de naissance</span>
                  <span className="text-gray-300">{p('birth_date')}</span>
                </div>
              )}
              {p('phone') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Telephone</span>
                  <span className="text-gray-300 font-mono">{p('phone')}</span>
                </div>
              )}
              {p('email') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Email</span>
                  <span className="text-gray-300 font-mono">{p('email')}</span>
                </div>
              )}
              {p('bio') && (
                <div className="pt-2 border-t border-border-subtle">
                  <p className="text-xs text-gray-400 leading-relaxed">{p('bio').substring(0, 300)}</p>
                </div>
              )}
              {p('occupation') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Occupation</span>
                  <span className="text-gray-300">{p('occupation')}</span>
                </div>
              )}
              {p('gender') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Genre</span>
                  <span className="text-gray-300">{p('gender')}</span>
                </div>
              )}
              {p('registration_number') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">N. Registre</span>
                  <span className="text-gray-300">{p('registration_number')}</span>
                </div>
              )}
              {p('jurisdiction') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Juridiction</span>
                  <span className="text-gray-300">{p('jurisdiction')}</span>
                </div>
              )}
              {p('industry') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Secteur</span>
                  <span className="text-gray-300">{p('industry')}</span>
                </div>
              )}
              {p('address') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Adresse</span>
                  <span className="text-gray-300">{p('address')}</span>
                </div>
              )}
              {p('city') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Ville</span>
                  <span className="text-gray-300">{p('city')}</span>
                </div>
              )}
              {p('country') && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Pays</span>
                  <span className="text-gray-300">{p('country')}</span>
                </div>
              )}
              {/* Confidence & dates */}
              <div className="pt-2 border-t border-border-subtle space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Confiance</span>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-surface-primary rounded-full overflow-hidden">
                      <div className="h-full bg-cyber-cyan rounded-full" style={{ width: `${entity.confidence * 100}%` }} />
                    </div>
                    <span className="text-gray-300">{Math.round(entity.confidence * 100)}%</span>
                  </div>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Cree le</span>
                  <span className="text-gray-300">{new Date(entity.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>
            </div>
          </InfoCard>

          {/* Companies / Emploi */}
          {categories && categories.companies.length > 0 && (
            <InfoCard
              title="Entreprises & Emploi"
              icon={TYPE_ICONS.organization}
              count={categories.companies.length}
            >
              <div className="space-y-2">
                {categories.companies.map((c, i) => (
                  <EntityBadge key={i} node={c.node} subtitle={c.role} />
                ))}
              </div>
            </InfoCard>
          )}

          {/* Assets / Patrimoine */}
          {categories && categories.assets.length > 0 && (
            <InfoCard
              title="Patrimoine & Actifs"
              icon={TYPE_ICONS.financial_account}
              count={categories.assets.length}
            >
              <div className="space-y-2">
                {categories.assets.map((a, i) => (
                  <EntityBadge key={i} node={a.node} subtitle={RELATION_LABELS[a.relation] || a.relation} />
                ))}
              </div>
            </InfoCard>
          )}

          {/* Locations */}
          {categories && categories.locations.length > 0 && (
            <InfoCard
              title="Localisations"
              icon={TYPE_ICONS.location}
              count={categories.locations.length}
            >
              <div className="space-y-2">
                {categories.locations.map((l, i) => (
                  <EntityBadge key={i} node={l.node} subtitle={l.relationType} />
                ))}
              </div>
            </InfoCard>
          )}

          {/* Family */}
          {categories && categories.family.length > 0 && (
            <InfoCard
              title="Famille"
              icon="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
              count={categories.family.length}
            >
              <div className="space-y-2">
                {categories.family.map((f, i) => (
                  <EntityBadge key={i} node={f.node} subtitle={f.relation} />
                ))}
              </div>
            </InfoCard>
          )}

          {/* Associates */}
          {categories && categories.associates.length > 0 && (
            <InfoCard
              title="Associes & Entourage"
              icon="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
              count={categories.associates.length}
            >
              <div className="space-y-2">
                {categories.associates.map((a, i) => (
                  <EntityBadge key={i} node={a.node} subtitle={a.relation} />
                ))}
              </div>
            </InfoCard>
          )}

          {/* Social Profiles */}
          {props.social_profiles && typeof props.social_profiles === 'object' && Object.keys(props.social_profiles as object).length > 0 && (
            <InfoCard
              title="Profils sociaux"
              icon="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
              count={Object.keys(props.social_profiles as object).length}
            >
              <div className="space-y-1.5">
                {Object.entries(props.social_profiles as Record<string, unknown>).map(([platform, data]) => {
                  const url = typeof data === 'string' ? data : (data as Record<string, unknown>)?.url || (data as Record<string, unknown>)?.profile_url || '';
                  return (
                    <div key={platform} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-surface-tertiary/50">
                      <span className="text-gray-400 capitalize">{platform}</span>
                      {url ? (
                        <span className="text-cyber-cyan truncate max-w-[60%]">{String(url)}</span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </InfoCard>
          )}

          {/* Enrichment Summary */}
          {props.enrichment_summary && typeof props.enrichment_summary === 'object' && (
            <InfoCard
              title="Enrichissement OSINT"
              icon="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
            >
              <div className="space-y-2 text-xs">
                {(props.enrichment_summary as Record<string, unknown>).total_accounts !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Comptes trouves</span>
                    <span className="text-gray-300 font-mono">{String((props.enrichment_summary as Record<string, unknown>).total_accounts)}</span>
                  </div>
                )}
                {Array.isArray((props.enrichment_summary as Record<string, unknown>).tools_used) && (
                  <div>
                    <span className="text-gray-500">Outils utilises</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {((props.enrichment_summary as Record<string, unknown>).tools_used as string[]).map((tool) => (
                        <span key={tool} className="px-1.5 py-0.5 text-xs bg-helios-700/30 text-helios-400 rounded">{tool}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </InfoCard>
          )}

          {/* Photos */}
          {Array.isArray(props.photos) && (props.photos as string[]).length > 0 && (
            <InfoCard
              title="Photos"
              icon="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M6.75 7.5l.75-.75"
              count={(props.photos as string[]).length}
            >
              <div className="grid grid-cols-3 gap-2">
                {(props.photos as string[]).slice(0, 6).map((url, i) => (
                  <div key={i} className="aspect-square rounded-lg overflow-hidden bg-surface-tertiary">
                    <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  </div>
                ))}
              </div>
            </InfoCard>
          )}

          {/* Risk Factors */}
          {riskData && riskData.factors.length > 0 && (
            <InfoCard
              title="Facteurs de risque"
              icon="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              count={riskData.factors.length}
            >
              <div className="space-y-2">
                {riskData.factors.map((f, i) => (
                  <div key={i} className="px-3 py-2 rounded-lg bg-surface-tertiary/50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-300">{f.name}</span>
                      <span className={`text-xs font-mono ${f.score >= 0.7 ? 'text-red-400' : f.score >= 0.4 ? 'text-amber-400' : 'text-green-400'}`}>
                        {Math.round(f.score * 100)}%
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-surface-primary rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${f.score >= 0.7 ? 'bg-red-500' : f.score >= 0.4 ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${f.score * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{f.details}</p>
                  </div>
                ))}
                {riskData.risk_level && (
                  <div className="flex justify-between text-xs pt-2 border-t border-border-subtle">
                    <span className="text-gray-500">Niveau global</span>
                    <span className={`font-semibold ${
                      riskData.risk_level === 'critical' ? 'text-red-400' :
                      riskData.risk_level === 'high' ? 'text-orange-400' :
                      riskData.risk_level === 'medium' ? 'text-amber-400' : 'text-green-400'
                    }`}>
                      {riskData.risk_level.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </InfoCard>
          )}

          {/* Network mini-preview */}
          <div className="lg:col-span-2 xl:col-span-3">
            <InfoCard
              title="Reseau de relations"
              icon="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
              count={graphEdges.length}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs text-gray-500">Profondeur :</span>
                {[3, 4, 5].map((d) => (
                  <button
                    key={d}
                    onClick={() => setNetworkDepth(d)}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                      networkDepth === d
                        ? 'bg-cyber-cyan/20 text-cyber-cyan'
                        : 'bg-surface-tertiary text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {d} niveaux
                  </button>
                ))}
                {networkLoading && (
                  <div className="w-4 h-4 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
                )}
                <span className="ml-auto text-xs text-gray-500">
                  {graphNodes.length} noeuds, {graphEdges.length} liens
                </span>
              </div>
              <div className="h-[500px] rounded-lg overflow-hidden border border-border-subtle bg-surface-primary">
                {graphNodes.length > 0 ? (
                  <ForceGraph
                    nodes={graphNodes}
                    edges={graphEdges}
                    selectedNodeId={id}
                    onNodeClick={(node) => {
                      if (node.id !== id) navigate(`/entities/${node.id}`);
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                    {networkLoading ? 'Chargement du reseau...' : 'Aucune relation trouvee'}
                  </div>
                )}
              </div>
            </InfoCard>
          </div>
        </div>
      )}

      {activeTab === 'timeline' && (
        <div className="space-y-4">
          {timelineLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
            </div>
          )}
          {!timelineLoading && (!timeline || timeline.events.length === 0) && (
            <p className="text-center text-gray-500 py-12">Aucun evenement dans la chronologie</p>
          )}
          {timeline && timeline.events.length > 0 && (
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-px bg-border-subtle" />
              <div className="space-y-4">
                {timeline.events.map((evt) => {
                  const severityColor =
                    evt.severity === 'critical' ? 'bg-red-500' :
                    evt.severity === 'high' ? 'bg-orange-500' :
                    evt.severity === 'medium' ? 'bg-amber-500' : 'bg-green-500';
                  return (
                    <div key={evt.id} className="relative flex gap-4 pl-3">
                      <div className={`w-3 h-3 rounded-full ${severityColor} flex-shrink-0 mt-1.5 z-10 ring-4 ring-surface-secondary`} />
                      <div className="glass-panel p-4 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className="text-sm font-medium text-gray-200">{evt.title}</h4>
                            <p className="text-xs text-gray-500 mt-0.5">{evt.description}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className="text-xs text-gray-500">{new Date(evt.created_at).toLocaleDateString('fr-FR')}</span>
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className={`px-1.5 py-0.5 text-xs rounded ${
                                evt.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                                evt.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                evt.severity === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'
                              }`}>
                                {evt.severity}
                              </span>
                              <span className="px-1.5 py-0.5 text-xs bg-surface-tertiary text-gray-400 rounded">{evt.event_type}</span>
                            </div>
                          </div>
                        </div>
                        {evt.source && (
                          <p className="text-xs text-gray-600 mt-2">Source: {evt.source}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="space-y-4">
          {/* Add note */}
          <div className="glass-panel p-4 space-y-3">
            <textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Ajouter une note ou observation..."
              className="w-full px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300 h-20 resize-none"
            />
            <div className="flex justify-between items-center">
              <button
                onClick={() => { if (id) enrichMutation.mutate({ entity_id: id }); }}
                disabled={enrichMutation.isPending}
                className="px-3 py-1.5 text-xs bg-helios-700/30 text-helios-400 rounded-lg hover:bg-helios-700/50 disabled:opacity-50"
              >
                {enrichMutation.isPending ? 'Enrichissement...' : 'Enrichir automatiquement'}
              </button>
              <button
                onClick={async () => {
                  if (id && noteContent.trim()) {
                    await createAnnotation.mutateAsync({ entity_id: id, content: noteContent, tags: [], annotation_type: 'note' });
                    setNoteContent('');
                    refetchAnnotations();
                  }
                }}
                disabled={!noteContent.trim() || createAnnotation.isPending}
                className="px-4 py-1.5 text-xs font-medium bg-cyber-cyan text-surface-primary rounded-lg hover:bg-cyan-400 disabled:opacity-50"
              >
                Publier
              </button>
            </div>
          </div>

          {/* Enrichment tasks */}
          {enrichTasks && Array.isArray(enrichTasks) && enrichTasks.length > 0 && (
            <div className="glass-panel p-4">
              <h4 className="text-xs font-semibold text-gray-400 mb-2">Enrichissements</h4>
              <div className="space-y-1.5">
                {enrichTasks.map((t: { id: string; source: string; status: string }) => (
                  <div key={t.id} className="flex items-center justify-between text-xs px-2 py-1.5 bg-surface-tertiary/50 rounded">
                    <span className="text-gray-300">{t.source}</span>
                    <span className={`px-1.5 py-0.5 rounded ${t.status === 'completed' ? 'bg-green-500/20 text-green-400' : t.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {t.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Existing notes */}
          {annotations && annotations.length > 0 ? (
            <div className="space-y-3">
              {annotations.map((note) => (
                <div key={note.id} className="glass-panel p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-cyber-cyan/10 flex items-center justify-center text-xs font-bold text-cyber-cyan">
                        {note.user_id.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-300">{note.user_id}</span>
                        <span className="text-xs text-gray-600 ml-2">{new Date(note.created_at).toLocaleString('fr-FR')}</span>
                      </div>
                    </div>
                    <span className="px-1.5 py-0.5 text-xs bg-surface-tertiary text-gray-400 rounded">{note.annotation_type}</span>
                  </div>
                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{note.content}</p>
                  {note.tags.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {note.tags.map((tag) => (
                        <span key={tag} className="px-1.5 py-0.5 text-xs bg-helios-700/30 text-helios-400 rounded">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">Aucune note pour cette entite</p>
          )}
        </div>
      )}

      {activeTab === 'network' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400">Profondeur du reseau :</span>
            {[1, 2, 3, 4, 5].map((d) => (
              <button
                key={d}
                onClick={() => setNetworkDepth(d)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  networkDepth === d
                    ? 'bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/30'
                    : 'bg-surface-tertiary text-gray-400 hover:text-gray-200'
                }`}
              >
                {d}
              </button>
            ))}
            {networkLoading && (
              <div className="w-4 h-4 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin ml-2" />
            )}
            <span className="ml-auto text-sm text-gray-500">
              {graphNodes.length} noeuds | {graphEdges.length} liens
            </span>
          </div>

          <div className="h-[700px] glass-panel overflow-hidden">
            {graphNodes.length > 0 ? (
              <ForceGraph
                nodes={graphNodes}
                edges={graphEdges}
                selectedNodeId={id}
                onNodeClick={(node) => {
                  if (node.id !== id) navigate(`/entities/${node.id}`);
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                {networkLoading ? 'Chargement...' : 'Aucune relation'}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'relations' && (
        <div className="space-y-4">
          {Object.keys(groupedRelations).length === 0 && (
            <p className="text-center text-gray-500 py-12">Aucune relation trouvee</p>
          )}
          {Object.entries(groupedRelations).map(([relType, items]) => (
            <div key={relType} className="glass-panel p-4">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-gray-200">
                  {RELATION_LABELS[relType] || relType.replace(/_/g, ' ')}
                </h3>
                <span className="px-2 py-0.5 text-xs bg-helios-700/30 text-helios-400 rounded-full">{items.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                {items.map((item, i) => (
                  <EntityBadge
                    key={i}
                    node={item.entity}
                    subtitle={`${item.direction === 'out' ? '\u2192' : '\u2190'} ${(item.relation.properties?.role as string) || (item.relation.properties?.relation as string) || ''}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'raw' && (
        <div className="glass-panel p-4">
          <h3 className="text-sm font-semibold text-gray-200 mb-3">Donnees brutes de l'entite</h3>
          <pre className="text-xs text-gray-400 bg-surface-primary rounded-lg p-4 overflow-auto max-h-[600px] font-mono">
            {JSON.stringify(entity, null, 2)}
          </pre>
          {network && (
            <>
              <h3 className="text-sm font-semibold text-gray-200 mt-6 mb-3">Reseau ({networkDepth} niveaux)</h3>
              <pre className="text-xs text-gray-400 bg-surface-primary rounded-lg p-4 overflow-auto max-h-[600px] font-mono">
                {JSON.stringify({ nodes: network.nodes?.length, edges: network.edges?.length, stats: network.stats }, null, 2)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}
